import { EntityId } from "@reduxjs/toolkit";

import { IconButton, Box, List, Divider } from "@mui/material";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";

import { useAppDispatch, useAppSelector } from "app/hooks";
import { coreActions } from "features/core/store/slice";
import {
    selectHideNavigationalElements,
    selectInFullscreenMode,
} from "features/core/store/selectors";

import { SiteControls } from "./SiteControls";
import { DrawerHeader } from "./DrawerHeader";

import { SiteList } from "features/sites/SiteList";
import { DeviceListSidebarMenu } from "features/devices/DeviceListSidebarMenu";
import { selectDevicesBySiteIdSortedByName } from "features/devices/store/selectors";

export interface SidebarProps {
    activeSiteId?: EntityId;
    onSiteSelected: (siteId: EntityId) => void;
    onSiteClosed: (siteId: EntityId) => void;

    selectedDeviceIds?: EntityId[];
    onToggleDevice: (
        siteId: EntityId,
        deviceId: EntityId,
        selected: boolean,
    ) => void;
    onSetSelectionStateOfAllDevicesInSite: (
        siteId: EntityId,
        selected: boolean,
    ) => void;

    thumbnailRefreshIntervalInMs?: number;
}

export function Sidebar(props: SidebarProps) {
    const dispatch = useAppDispatch();

    const inFullscreenMode = useAppSelector(selectInFullscreenMode);

    const hideNavigationalElements = useAppSelector(
        selectHideNavigationalElements,
    );

    const devicesSortedByName = useAppSelector((state) =>
        props.activeSiteId
            ? selectDevicesBySiteIdSortedByName(state, props.activeSiteId)
            : [],
    );

    return (
        <>
            {inFullscreenMode && (
                <DrawerHeader>
                    <IconButton
                        onClick={() => {
                            dispatch(
                                coreActions.setShowSidebar({
                                    show: false,
                                }),
                            );
                        }}
                    >
                        <KeyboardDoubleArrowLeftIcon />
                    </IconButton>
                </DrawerHeader>
            )}

            <Box
                sx={{
                    overflow: "auto",
                    "&::-webkit-scrollbar": { display: "none" },
                }}
            >
                <List>
                    <SiteList
                        onSiteSelected={props.onSiteSelected}
                        onSiteClosed={props.onSiteClosed}
                        activeSiteId={props.activeSiteId}
                        hideCloseButton={hideNavigationalElements}
                    />
                </List>

                {props.activeSiteId && (
                    <>
                        <Divider
                            sx={{
                                marginBottom: 2,
                            }}
                        />
                        <List>
                            <DeviceListSidebarMenu
                                activeSiteId={props.activeSiteId}
                                devices={devicesSortedByName}
                                onToggleDevice={props.onToggleDevice}
                                onSetSelectionStateOfAllDevicesInSite={
                                    props.onSetSelectionStateOfAllDevicesInSite
                                }
                                selectedDeviceIds={props.selectedDeviceIds}
                                thumbnailRefreshIntervalInMs={
                                    props.thumbnailRefreshIntervalInMs || 0
                                }
                            />
                            <SiteControls
                                siteId={props.activeSiteId}
                                hidePlaybackButtons={hideNavigationalElements}
                            />
                        </List>
                    </>
                )}
            </Box>
        </>
    );
}
